/**
 * Inbox Preferences Service
 * Persist filter preferences, saved views and sort config per user.
 */
import { authFetch } from '@/utils/api';
import { normalizeInboxPreferences } from '../utils/inboxPreferences';

/**
 * Fetch current user's inbox preferences
 * @param {string} userId
 * @returns {Promise<Object>}
 */
export async function getInboxPreferences(userId) {
    try {
        const res = await authFetch(`/api/users/${userId}/inbox-preferences`);
        if (!res.ok) return {};
        const data = await res.json();
        return normalizeInboxPreferences(
            data.inboxPreferences || {},
            DEFAULT_INBOX_PREFERENCES,
        );
    } catch (e) {
        console.error('[InboxPreferences] fetch error:', e);
        return {};
    }
}

/**
 * Update user's inbox preferences (partial merge on server)
 * @param {string} userId
 * @param {Object} prefs - partial preferences to merge
 * @returns {Promise<Object>}
 */
export async function updateInboxPreferences(userId, prefs) {
    try {
        const res = await authFetch(`/api/users/${userId}/inbox-preferences`, {
            method: 'PATCH',
            body: JSON.stringify(
                normalizeInboxPreferences(prefs, DEFAULT_INBOX_PREFERENCES),
            ),
        });
        if (!res.ok) throw new Error('Failed to save');
        const data = await res.json();
        return normalizeInboxPreferences(
            data.inboxPreferences || {},
            DEFAULT_INBOX_PREFERENCES,
        );
    } catch (e) {
        console.error('[InboxPreferences] update error:', e);
        throw e;
    }
}

/**
 * Default inbox preferences structure
 */
export const DEFAULT_INBOX_PREFERENCES = {
    activeFilters: {},
    savedViews: [],
    defaultSort: 'lastMessageAt',
    defaultSortDir: 'desc',
    sectionsCollapsed: {
        waiting: false,
        mine: true,
        closed: true,
    },
    hiddenBuiltinViews: [],
};

/**
 * Built-in views available for all users
 */
export const BUILTIN_VIEWS = [
    {
        id: '__waiting',
        name: 'Aguardando Atendimento',
        icon: 'Clock',
        filters: { status: 'new', assignedTo: 'none' },
        builtIn: true,
    },
    {
        id: '__mine',
        name: 'Minhas conversas',
        icon: 'UserCheck',
        filters: { status: 'open', assignedTo: 'me' },
        builtIn: true,
    },
    {
        id: '__all_open',
        name: 'Todos em atendimento',
        icon: 'Users',
        filters: { status: 'open', assignedTo: 'any' },
        builtIn: true,
        minLevel: 30, // supervisor+
        allowSpy: true, // e quem tem o modo espião (tickets.view_other_departments)
    },
    {
        id: '__closed',
        name: 'Finalizados',
        icon: 'Archive',
        filters: { status: 'closed' },
        builtIn: true,
    },
];

/**
 * Available icon names for saved views
 */
export const VIEW_ICONS = [
    'Flame', 'Star', 'Heart', 'Zap', 'Shield', 'Flag',
    'Target', 'Bell', 'Bookmark', 'Tag', 'Folder',
    'MessageCircle', 'AlertTriangle', 'Clock', 'Users',
    'Building2', 'Globe', 'Lock', 'Eye', 'Filter',
];
